import type {
  Booking,
  BookingStatus,
  DailySeriesRow,
  DateWindow,
  HealthIssue,
  OwnerContact,
  Payment,
  StatStripData,
} from './types';
import {
  DOW_ABBR,
  MONTHS_ABBR,
  addDays,
  addMonth,
  endOfMonth,
  isoDate,
  parseLocal,
  startOfDay,
  startOfMonth,
} from '../../lib/dates';
import { toMoney } from '../../lib/money';
// The server's own "does this amount clear the row?" rule, shared rather than
// re-implemented — see clearsRecordedPayment.
import { clearsPayment, isClosedStatus } from '@bbbk/schema';

// Booking-domain rules — pricing, payment, occupancy, stat-strip, vaccine
// parsing. Generic date maths lives in lib/dates.ts; money parsing/formatting
// in lib/money.ts. This module owns everything that knows what a BOOKING is.

// PRICING MOVED to @bbbk/schema (portal-foundations AC8) — the customer portal
// has to quote the same price the desk does, and it cannot import a module from
// inside the admin SPA. Re-exported here rather than deleted so every existing
// import site and both existing pricing suites keep working against the SAME
// functions, which is precisely how AC8 asks the move to be proven.
export {
  calcBookingTotal,
  perDogNightly,
  perNightForDogs,
  SHARED_KENNEL_DISCOUNT,
} from '@bbbk/schema';
// …and imported, because deskPayAction below computes with it.
import { calcBookingTotal, AREAS } from '@bbbk/schema';

// AM/PM label for a drop-off / collection value — carried from _prototype
// periodOf. Accepts 'am'/'pm' or a HH:MM time (legacy import rows).
export function periodOf(v: string | null | undefined): string {
  if (!v) return '';
  if (v === 'am') return 'AM';
  if (v === 'pm') return 'PM';
  return v < '12:00' ? 'AM' : 'PM';
}

// Payment status with the legacy fallback — carried from _prototype
// derivePaymentStatus: explicit status wins; else paid flag; else deposit > 0.
export function derivePaymentStatus(
  b: { paymentStatus?: string | null; paid?: boolean; deposit?: string | number | null } | null | undefined,
): 'unpaid' | 'partial' | 'paid' {
  if (!b) return 'unpaid';
  if (b.paymentStatus === 'unpaid' || b.paymentStatus === 'partial' || b.paymentStatus === 'paid')
    return b.paymentStatus;
  if (b.paid === true) return 'paid';
  if (toMoney(b.deposit) > 0) return 'partial';
  return 'unpaid';
}

// `initialDeposit` lived here: it seeded the modal's single deposit field from
// the stored `deposit` mirror. The modal now opens on the LEDGER itself, split
// into deposit and remainder by `splitLedger` below, so the mirror no longer
// seeds anything and the helper had no callers left.

// `depositAdjustment`, `ledgerWritesFor` and `splitLedger` lived here. They
// served the TWO-BUCKET modal, where each field was a target and the save
// wrote the DIFFERENCE into its bucket. The modal now shows one field per
// payment and each writes an ABSOLUTE amount to its own row (PATCH), so there
// is no difference to compute and no bucket to sum — all three lost their
// callers. See ledgerFields above.

/**
 * What the Daily desk's pay control should say, and do — ONE decision, consumed
 * by both the label and the click.
 *
 * They used to compute the figure separately, which is exactly how the
 * overpayment bug arrived: the label learned to read "€50 back" while the click
 * still followed the old "paid ⇒ un-settle" rule, so it either did nothing (no
 * desk row to delete) or deleted a whole settlement the label never mentioned.
 *
 * - `collect`  — money is owed. POST /settle.
 * - `credit`   — MORE was taken than the stay cost. REPORTED ONLY, never
 *                actioned (Freddi, 2026-07-31): the desk tap settles a bill, and
 *                money owed back to a customer is resolved in the booking modal
 *                where the individual payments can be corrected. `actionable`
 *                is false, so the control shows the figure and refuses the press.
 * - `unsettle` — square to the penny; the tap means "undo the settlement".
 *                DELETE /settle.
 *
 * `amount` is always positive — the direction lives in `act`, so no caller has
 * to remember which way a sign points. It is for DISPLAY only: every write
 * recomputes server-side under a row lock, because the desk's copy of a balance
 * can be seconds stale and a stale figure here is money invented.
 */
export function deskPayAction(
  b: { total?: string | number | null; deposit?: string | number | null; area: string; checkIn: string; checkOut: string; dogs: { id: string }[] },
): { act: 'collect' | 'credit' | 'unsettle'; amount: number; actionable: boolean } {
  const calc = calcBookingTotal({ area: b.area, checkIn: b.checkIn, checkOut: b.checkOut, dogs: b.dogs.length });
  const stored = b.total != null ? Number(b.total) : Number.NaN;
  const total = Number.isFinite(stored) && stored > 0 ? stored : calc.total;
  const balance = amountDue({ total, deposit: b.deposit });
  if (balance > 0) return { act: 'collect', amount: balance, actionable: true };
  if (balance < 0) return { act: 'credit', amount: -balance, actionable: false };
  return { act: 'unsettle', amount: 0, actionable: true };
}

export type LedgerBucket = 'deposit' | 'remainder';

export type LedgerField = {
  /** The payment this field edits, or null for one not recorded yet. */
  paymentId: string | null;
  label: string;
  amount: number;
  /** Only used when this field has to CREATE its payment. */
  kind: LedgerBucket;
};

/**
 * The booking modal's money fields — ONE PER PAYMENT (Freddi, 2026-07-31).
 *
 * Summing everything after the deposit into a single "Remainder paid" hid real
 * transactions: a deposit, a part-payment that did not settle the bill, and then
 * a desk settlement showed as two figures instead of three.
 *
 * The labels are POSITIONAL, because that is how the money reads back to
 * whoever took it: the first payment is the DEPOSIT, the last is the REMAINDER
 * (the one that squares the bill, which the desk's Paid tap fills in), and
 * anything between is numbered from 2.
 *
 * A deposit and a remainder are ALWAYS offered, bound to a payment when one
 * exists and open otherwise — so a booking with nothing recorded still has
 * somewhere to type, and one with only a deposit still has somewhere to settle.
 *
 * `payments` arrives newest-first from the API; these come back oldest-first,
 * which is the order the money actually arrived in.
 *
 * VOIDED ROWS ARE NOT PAYMENTS. A row cleared as an entry error keeps its place
 * in the table so the ledger can say it happened and was undone, but it holds no
 * slot here: it must not be editable, must not number a middle field, and must
 * not occupy an end slot that the correct figure now needs. See voidedPayments
 * for the history line that shows it instead.
 */
export function ledgerFields(payments: readonly Payment[] | null | undefined): LedgerField[] {
  const rows = [...(payments ?? [])].filter((p) => !p.voidedAt).reverse(); // live, oldest first
  const first = rows[0];
  const last = rows[rows.length - 1];
  // POSITION ALONE IS NOT ENOUGH. "Pay at collection" is an ordinary booking: no
  // advance deposit, so the create writes no payment row, and the desk's Paid tap
  // later inserts a single `remainder` row for the whole total. Labelling by
  // index called that row "Deposit paid" — the exact opposite of what happened.
  // A row may only occupy an end slot if its own `kind` says it belongs there;
  // otherwise the slot stays OPEN, which is also how "nothing has settled the
  // bill yet" gets somewhere to type.
  const depositRow = first && first.kind === 'deposit' ? first : null;
  const remainderRow = last && last.kind === 'remainder' ? last : null;
  const middle = rows.slice(depositRow ? 1 : 0, remainderRow ? rows.length - 1 : rows.length);
  return [
    { paymentId: depositRow?.id ?? null, label: 'Deposit paid', amount: depositRow?.amount ?? 0, kind: 'deposit' },
    ...middle.map((r, i) => ({
      paymentId: r.id,
      label: `Payment ${i + 2}`,
      amount: r.amount,
      kind: 'remainder' as const, // never created from a middle slot; it is always bound
    })),
    { paymentId: remainderRow?.id ?? null, label: 'Remainder paid', amount: remainderRow?.amount ?? 0, kind: 'remainder' },
  ];
}


/**
 * A fingerprint of the ledger, for the booking modal's React key.
 *
 * `BookingForm` seeds its field values in a `useState` initialiser — once, at
 * mount. Keyed on the booking id alone, fresher data for the SAME booking never
 * re-seeds them, so marking a stay paid on the Daily desk left the modal showing
 * the figures from before the settlement. Including this in the key remounts the
 * form exactly when the ledger moves underneath it, and never otherwise: a
 * re-render with an unchanged ledger must not throw away what someone is halfway
 * through typing.
 */
export function ledgerSignature(payments: readonly Payment[] | null | undefined): string {
  // `voidedAt` is part of the fingerprint because clearing a payment moves the
  // ledger without changing a single amount. Left out, the re-seed never fires
  // and the modal goes on showing a figure that has just been cleared — the
  // desk-settlement staleness this function exists to prevent, in a new coat.
  return (payments ?? []).map((p) => `${p.id}:${p.amount}:${p.voidedAt ?? ''}`).join('|');
}

/**
 * The payments CLEARED as entry errors, oldest first — for display only.
 *
 * They are shown as history beneath the editable fields rather than hidden: a
 * void nobody can see is indistinguishable from the silent deletion it replaced,
 * and being able to see it is what makes clearing a payment safe to do in one
 * press instead of behind a confirmation step (Freddi, 2026-08-15).
 */
export function voidedPayments(
  payments: readonly Payment[] | null | undefined,
): (Payment & { voidedAt: string })[] {
  // The return type NARROWS `voidedAt` to a string, so the caller rendering the
  // date needs no non-null assertion to say what this filter already guarantees.
  return [...(payments ?? [])]
    .filter((p): p is Payment & { voidedAt: string } => !!p.voidedAt)
    .reverse();
}

/**
 * The stay's price, for display AND for the save payload.
 *
 * The STORED total is the agreed price, and it is the figure the Daily desk's
 * Paid tap pays off. Recomputing it as nights × rate made the modal disagree
 * with itself: a booking with no stored rate seeds that field as
 * round(total / nights), so €175 over 4 nights seeds €44 and recomputes to €176.
 * A fully settled stay then read €1 outstanding — or, the other way, as a
 * phantom overpayment with a credit owed. Nobody mis-paid; the modal was
 * measuring against a different number from the one that was settled. Worse, it
 * travels in the PATCH payload, so a notes-only save rewrote the agreed price.
 *
 * But the pin holds only while the STAY is unchanged. Anything that genuinely
 * re-prices it must recompute: an edited rate, and equally a change of dates —
 * extending a stay by two nights with the rate untouched was leaving them
 * unbilled, which is the same class of silent-money bug in the other direction.
 */
export function stayTotal(a: {
  isNew: boolean;
  rateTouched: boolean;
  datesChanged: boolean;
  storedTotal: number | null;
  nights: number | null;
  rate: number;
}): number {
  const pinned = !a.isNew && !a.rateTouched && !a.datesChanged && a.storedTotal != null;
  if (pinned) return a.storedTotal!;
  return a.nights != null ? Math.round(a.nights * a.rate) : 0;
}

/**
 * Does this payment CLOSE the bill?
 *
 * `kind` is what decides which end slot a row may occupy, and it must follow the
 * money rather than the box the figure was typed into. Recording a second,
 * partial payment through the trailing "Remainder paid" slot used to mark it
 * `remainder`, so it took the settling position; the desk's Paid tap then found
 * nothing outstanding, wrote no row, and the third payment never existed. The
 * modal honestly showed two fields for two payments while the flow that should
 * have produced three could not.
 *
 * Compared in CENTS: 0.1 + 0.2 is 0.30000000000000004, and a stay would
 * otherwise fail to settle by a millionth of a penny.
 */
export function settlingKind(receivedAfter: number, total: number): LedgerBucket {
  if (total <= 0) return 'deposit'; // nothing to close
  return Math.round(receivedAfter * 100) >= Math.round(total * 100) ? 'remainder' : 'deposit';
}

/**
 * The `kind` for a payment being CREATED from one of the modal's open slots.
 *
 * The asymmetry here is deliberate, and was asked for as a decision in review.
 * The two open slots express different facts about the same money — "taken as a
 * deposit" and "collected to close the bill" — and which one staff typed into is
 * information, not noise. So the LEADING slot records a deposit even when the
 * figure covers the whole stay: money taken up front is a deposit whether or not
 * it settles. Only the TRAILING slot can claim the settling position, and only
 * when the money says it does.
 *
 * Running settlingKind on both would make a full up-front payment reappear under
 * "Remainder paid" — the figure jumping to a different field from the one it was
 * entered in. Neither choice affects money: a `deposit`-kind row can never bind
 * the trailing slot, so the desk's Paid tap always finds the true balance.
 */
export function kindForNewPayment(slot: LedgerBucket, receivedAfter: number, total: number): LedgerBucket {
  return slot === 'deposit' ? 'deposit' : settlingKind(receivedAfter, total);
}

export type MoneyFieldState = { keys: string[]; amounts: string[]; touched: boolean[] };

/** A field's IDENTITY — the payment it edits, or the open slot it is. */
export function fieldKey(f: LedgerField): string {
  return f.paymentId ?? `open:${f.label}`;
}

/**
 * Re-seed the modal's money fields after the ledger moves underneath them.
 *
 * Untouched fields take the server's figure — that is the whole point, so a
 * settlement taken at the Daily desk shows here at once. A field the user is
 * mid-way through correcting keeps what they typed.
 *
 * PRESERVATION IS BY IDENTITY, NOT POSITION, and that distinction is the
 * difference between a stale readout and destroyed money. The open "Remainder
 * paid" slot always sits last. Touch it, then let the desk settle the booking:
 * by index, the typed figure would be kept AND would now carry the desk row's
 * id, so the next save would `PATCH` a real settlement down to whatever was
 * half-typed — with the modal already closed and no undo. A slot that has just
 * bound to a payment is a DIFFERENT field and takes the server's figure.
 */
export function reseedMoney(prev: MoneyFieldState, fields: readonly LedgerField[]): MoneyFieldState {
  const was = new Map(prev.keys.map((k, i) => [k, i]));
  const at = (f: LedgerField) => was.get(fieldKey(f));
  return {
    keys: fields.map(fieldKey),
    amounts: fields.map((f) => {
      const i = at(f);
      return i !== undefined && prev.touched[i] ? (prev.amounts[i] ?? '') : String(f.amount);
    }),
    touched: fields.map((f) => {
      const i = at(f);
      return i !== undefined ? (prev.touched[i] ?? false) : false;
    }),
  };
}

/**
 * Would saving this field CLEAR the payment behind it?
 *
 * `parseMoneyText` reads a blank box as zero, and a bound field cleared to zero
 * is not an edit — the ledger forbids a zero-amount row. It used to be REFUSED:
 * Save greyed out, with the reasoning that removing a payment belonged to its own
 * deliberate control. That control lives in `PaymentsPanel`, which the modal has
 * never mounted, so the rule closed a loop with no way out and a payment entered
 * in error was permanent (Freddi, 2026-08-15).
 *
 * Clearing now WRITES: the row is voided, keeping its amount and its date on the
 * ledger. Saving a zero to `PATCH /payments/:paymentId` is what performs it.
 *
 * ONE HOME, because the modal asks this question TWICE and from opposite ends —
 * when labelling the field before Save, and when naming the write inside it. Both
 * sites carried the rule inline and would have drifted the first time either one
 * changed. `null` (mid-keystroke) is not a clear — Save still blocks on it — and
 * an OPEN slot at zero is just the resting state.
 *
 * THE AMOUNT TEST IS THE SERVER'S OWN (`clearsPayment` in `@bbbk/schema`), not a
 * comparison against an exact zero. The server rounds to cents before deciding,
 * so `0.004` clears the row — and while this function tested `value === 0` the
 * modal promised "recorded a correction" and the payment vanished (PR #159
 * review, both reviewers). One rule, imported, so they cannot disagree again.
 */
/**
 * The replay key for a field that will APPEND a payment — one per field, minted
 * on first use and remembered.
 *
 * ONE SLOT WAS NOT ENOUGH (PR #161 review, both reviewers). A single
 * `useRef<string|null>` held the key while the money writes ran one at a time.
 * They now run together, and `Array.map` drives every async callback up to its
 * first `await` before any of them resumes — so the second append found the slot
 * already filled, reused the first one's key, and the server's
 * `ON CONFLICT (client_key) DO NOTHING` discarded it as a replay. A booking with
 * neither figure recorded yet offers TWO open slots, so entering both and saving
 * silently kept one payment and threw the other away, under a success toast.
 *
 * Keyed by the field's own identity — the same `fieldKey` the re-seed uses — so
 * a retry of the SAME field still replays safely, which is what the key is for,
 * while two different fields can never collide.
 */
export function replayKeyFor(keys: Map<string, string>, field: LedgerField, mint: () => string): string {
  const k = fieldKey(field);
  const existing = keys.get(k);
  if (existing) return existing;
  const fresh = mint();
  keys.set(k, fresh);
  return fresh;
}

/** One touched money field, paired with the figure the save will write. */
export type MoneyEdit = { field: LedgerField; value: number };

/**
 * Split a save's money edits into what may run CONCURRENTLY and what must not.
 *
 * PATCHes — fields bound to an existing row — are absolute, idempotent, and
 * touch different rows, so they go together.
 *
 * APPENDS — open slots writing a new row — are different (PR #161 review):
 *
 * - Their ORDER IS THE LEDGER. `ledgerFields` binds the deposit slot to the
 *   OLDEST row and the remainder slot to the NEWEST, so two appends racing let
 *   the remainder commit first and carry the earlier `created_at` — after which
 *   both real payments read back as numbered middle rows with both end slots
 *   open at €0, inviting the money to be recorded again. Sequential, in field
 *   order (the ledger's order), the timestamps match the slots.
 * - Their KIND is classified against `received`, the sum of every edited field —
 *   money the siblings have not yet committed. Appends therefore run only after
 *   every patch has succeeded, so the sum they are classified against is money
 *   actually on the ledger; a failed patch stops the save before the append can
 *   claim the settling slot for a payment that settles nothing.
 *
 * The zero-value open slot is dropped here — it is the resting state, not a
 * write — so the caller never re-tests it.
 */
export function partitionMoneyEdits(edits: readonly MoneyEdit[]): {
  patches: MoneyEdit[];
  appends: MoneyEdit[];
} {
  return {
    patches: edits.filter((e) => e.field.paymentId !== null),
    appends: edits.filter((e) => e.field.paymentId === null && e.value !== 0),
  };
}

export function clearsRecordedPayment(field: LedgerField, value: number | null): boolean {
  return field.paymentId !== null && value !== null && clearsPayment(value);
}

// The booking's balance for the "Due" column — total minus the deposit recorded.
// A NEGATIVE deposit is arrears carried from an unpaid previous stay, so it ADDS
// to the due (total €100, deposit −€50 → €150). It can also go negative the other
// way: a cash deposit larger than the stay cost shows as a credit owed back
// (total €100, deposit €150 → −€50) rather than being hidden as €0.
export function amountDue(
  b: { total?: string | number | null; deposit?: string | number | null },
): number {
  return toMoney(b.total) - toMoney(b.deposit);
}

// ---------- Windowed-fetch ranges (BUG-CHARTS-WINDOW) ----------
//
// Calendar, charts, and the stat strip are date-relevant views — they must be
// fed bookings overlapping a date window, NOT the recency-capped list (whose
// 200 newest-by-created_at rows are all historical import data). These two
// helpers produce the `from`/`to` the server's daterange-overlap query takes.

// Fixed "today" snapshot window for the stat strip. Spans today-2 → today+2 so a
// single overlap query returns everything computeStats needs: bookings in
// residence today (they overlap the window), and arrivals/departures for
// yesterday, today, and tomorrow. from = today-2 (not today-1) so a booking
// *leaving* yesterday — checkOut = today-1 — still overlaps the window;
// to = today+2 covers tomorrow's arrivals and departures. Independent of
// calendar nav so the snapshot never moves when the user pages the month.
export function statWindow(today: string | number | Date = new Date()): {
  from: string;
  to: string;
} {
  const t = startOfDay(today);
  return { from: isoDate(addDays(t, -2)), to: isoDate(addDays(t, 2)) };
}

// Anchored month window for calendar + charts. Bookings overlapping any day of
// the anchored month, so a stay that begins in the previous month still shows
// its in-month days.
export function monthWindow(anchor: string | number | Date): { from: string; to: string } {
  return { from: isoDate(startOfMonth(anchor)), to: isoDate(endOfMonth(anchor)) };
}

// Forward window: today → the same day three months ahead. Feeds the Today
// page's upcoming-occupancy chart. The end day is clamped to the target month's
// length, so 30 Nov + 3mo lands on 28/29 Feb rather than rolling into March.
export function quarterWindow(today: string | number | Date = new Date()): {
  from: string;
  to: string;
} {
  const t = startOfDay(today);
  const [y, m] = addMonth(t.getFullYear(), t.getMonth(), 3);
  const lastDay = new Date(y, m + 1, 0).getDate();
  const day = Math.min(t.getDate(), lastDay);
  return { from: isoDate(t), to: isoDate(new Date(y, m, day)) };
}

// THE windowed-fetch ranges for a day-scoped page (Daily, Messages): the ±2-day
// stat window for `anchor` plus the adjacent days to cache-warm, so stepping one
// day resolves from cache and stays inside the held set (no blank/flicker). One
// source so the day-stepping pages can't drift apart on what they fetch.
export function dayStatWindows(anchor: string): { range: DateWindow; prefetch: DateWindow[] } {
  return {
    range: statWindow(anchor),
    prefetch: [statWindow(isoDate(addDays(anchor, -1))), statWindow(isoDate(addDays(anchor, 1)))],
  };
}

// How many CALENDAR DAYS a window covers, both ends included — one row per day,
// which is what every day-series chart draws. Distinct from nightsBetween, which
// counts the gaps rather than the days.
export function daysInWindow(from: string, to: string): number {
  return Math.max(1, nightsBetween(from, to) + 1);
}

export function nightsBetween(checkIn: string, checkOut: string): number {
  const a = parseLocal(checkIn);
  const b = parseLocal(checkOut);
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));
}

// The real nightly rate to DISPLAY for a booking: the stored household
// rate_per_night when set, else derived from total ÷ nights (older imports that
// predate the stored rate). Never the prototype-fiction calcBookingTotal (its
// per-dog ×0.6 was never the charged price). One source so the bookings table
// and the daily desk show the same number. Returns null when nothing usable.
export function displayRatePerNight(b: {
  ratePerNight?: string | null;
  total?: string | number | null;
  checkIn: string;
  checkOut: string;
}): number | null {
  const stored = parseFloat(String(b.ratePerNight ?? ''));
  if (Number.isFinite(stored) && stored > 0) return Math.round(stored);
  const nights = nightsBetween(b.checkIn, b.checkOut);
  const total = parseFloat(String(b.total ?? '')) || 0;
  return nights > 0 && total > 0 ? Math.round(total / nights) : null;
}

// Whether to PERSIST the nightly rate on save. A rate is only real when the
// booking is new, the user actually edited the rate, or the booking already
// stored one. For a legacy row with no stored rate, the form's `rate` is merely
// derived from total ÷ nights for display — persisting it would invent a rate and
// drift from the original total, so return null and let the total-fallback stay
// in charge (Codex review).
export function ratePerNightToPersist(o: {
  isNew: boolean;
  rateTouched: boolean;
  hadStoredRate: boolean;
  rateNum: number;
}): number | null {
  return o.isNew || o.rateTouched || o.hadStoredRate ? o.rateNum : null;
}

// Dogs on a single booking — the atomic count unit. A stay with no dog records
// still counts as one (`|| 1`): the legacy-import guard that keeps a dog-less
// stay from vanishing from the headcount, matching the server's
// GREATEST(dog_count, 1). Every dog tally builds on this so none can drift.
export function dogsOf(b: Pick<Booking, 'dogs'>): number {
  return b.dogs.length || 1;
}

// Sum dogs across a list of bookings — the calendar/desk day totals count DOGS,
// not stays (a 3-dog booking is 3 dogs). Built on dogsOf, so it can never diverge
// from the per-booking rule or the server count.
export function dogCount(list: Pick<Booking, 'dogs'>[]): number {
  return list.reduce((s, b) => s + dogsOf(b), 0);
}

// THE occupancy-span rule: a booking occupies a day iff it's a real (non-
// cancelled) stay and the day falls in [check-in, check-out) — checkout day
// excluded (a departure, not a night). Shared by occupancyByDate, the chart's
// area buckets, and the Today date-based projection, so a single day's "in
// residence" can never diverge across calendar / roster / today.
export function spansDay(b: Pick<Booking, 'status' | 'checkIn' | 'checkOut'>, day: string): boolean {
  // Closed — cancelled OR declined — spans nothing (PR #251 review).
  return !isClosedStatus(b.status) && isoDate(b.checkIn) <= day && day < isoDate(b.checkOut);
}

// THE departure rule: how many DOGS actually go home on `day`. Deliberately not
// the same question as "how many bookings check out" (that is collectingOn +
// dogCount, what the Collections band counts) — a dog rolling straight from one
// stay into the next has a checkout today but never leaves the kennel.
//
// The client mirror of countDogsLeavingOn in apps/api/src/messaging/brief-run.ts,
// clause for clause: checkout IS the day, the stay is a real night (day-boarders
// out), not cancelled, and the dog has no other stay still spanning the day —
// counting DISTINCT dogs. Anything telling staff how many dogs are going home
// must read THIS, so the message and their own briefing cannot disagree.
export function dogsLeavingCount(bookings: Booking[], day: string): number {
  const leaving = new Set<string>();
  for (const b of bookings) {
    if (isClosedStatus(b.status)) continue;
    if (isoDate(b.checkOut) !== day) continue;
    if (isoDate(b.checkIn) >= day) continue; // zero-night day-boarder
    for (const d of b.dogs) {
      // A stay of this dog that still spans the day is a continuation, so the
      // dog is resident, not leaving. spansDay carries the cancelled guard.
      const staysOn = bookings.some((cb) => cb.dogs.some((cd) => cd.id === d.id) && spansDay(cb, day));
      if (!staysOn) leaving.add(d.id);
    }
  }
  return leaving.size;
}

// Occupancy per day — the dogs that physically occupied each night, the measure
// the calendar shows ("N in") and the roster needs for staffing AND history.
// Every status that represents a real stay counts — unconfirmed/confirmed
// (expected), arrived (here), collected (was here) — so PAST months read true;
// only cancelled (the stay never happened) is excluded. This is the locked
// occupancy rule that reconciled 64/64 against the kennel's own dog-calendar;
// dropping collected blanked all history and diverged from it. Counts DOGS via
// spansDay + dogsOf — one source of truth so calendar and roster agree.
export function occupancyByDate(bookings: Booking[], dates: string[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const d of dates) map.set(d, 0);
  for (const b of bookings) {
    for (const d of dates) {
      if (spansDay(b, d)) map.set(d, (map.get(d) ?? 0) + dogsOf(b));
    }
  }
  return map;
}

// ---------- Display labels (carried from _prototype/components.jsx) ----------

// "Thu 4 Jun 2026" — THE day-nav label for the centred header date control.
// One source shared by every date-scoped page (Daily, Messages) so the header
// reads identically. Local calendar day (parseLocal) — never a UTC shift.
export function dayLabel(d: string | number | Date): string {
  const x = parseLocal(d);
  return `${DOW_ABBR[x.getDay()]} ${x.getDate()} ${MONTHS_ABBR[x.getMonth()]} ${x.getFullYear()}`;
}

// Compact stay range + nights — the single source for the desk's date label, so
// drop-offs and collections read identically. Same-month stays drop the leading
// month ("5–15 Jun · 10 nights"); a stay that crosses into the next month shows
// both ("23 May–5 Jun · 13 nights").
export function stayDatesLabel(checkIn: string, checkOut: string): string {
  const ci = parseLocal(checkIn);
  const co = parseLocal(checkOut);
  const sameMonth = ci.getMonth() === co.getMonth() && ci.getFullYear() === co.getFullYear();
  const from = sameMonth ? `${ci.getDate()}` : `${ci.getDate()} ${MONTHS_ABBR[ci.getMonth()]}`;
  const to = `${co.getDate()} ${MONTHS_ABBR[co.getMonth()]}`;
  return `${from}–${to}`;
}

// "1 night" / "10 nights" — THE nights-count label. One source for the desk
// rows, the bookings table, and the stay-range string so they can't drift.
export function nightsLabel(n: number): string {
  return `${n} ${n === 1 ? 'night' : 'nights'}`;
}

// "€45/night" — THE nightly-rate label, shared by the daily desk and the
// bookings table so the two always read identically.
export function nightlyRateLabel(rate: number): string {
  return `€${rate}/night`;
}

export function stayRangeLabel(checkIn: string, checkOut: string): string {
  return `${stayDatesLabel(checkIn, checkOut)} · ${nightsLabel(nightsBetween(checkIn, checkOut))}`;
}

// ---------- Owner label (carried from _prototype/bookings.jsx) ----------

// A deposit-less booking is unconfirmed; a deposit confirms it. This drives the
// create-default and the modal's auto-promote — but only ever PROMOTES from
// unconfirmed when money lands. It never downgrades, and never overrides a
// status staff have already moved on (arrived/collected/cancelled) or set by
// hand, so a manual override survives.
/**
 * Has the status in the modal's bar moved off the booking's saved one? Both
 * values come from the modal — the bar's state and the booking it holds — so
 * the answer never flickers through a stale prop the way a form-side snapshot
 * did (dog-size-gate-everywhere, 2026-10-08). A new booking's base is the
 * default the modal gives it; nothing else is being edited.
 */
export function statusIsDirty(mode: string, savedStatus: BookingStatus | null | undefined, status: BookingStatus): boolean {
  if (mode === 'new') return status !== 'unconfirmed';
  if (mode === 'booking') return !!savedStatus && status !== savedStatus;
  return false;
}

export function statusForDeposit(currentStatus: BookingStatus, deposit: number, source?: string | null): BookingStatus {
  // An app booking is confirmed by the server, against the deposit asked and the size gate (2026-10-11).
  if (source === 'portal') return currentStatus;
  if (currentStatus === 'unconfirmed' && deposit > 0) return 'confirmed';
  return currentStatus;
}

// The owner label for a booking/household. A couple (the common case) shows
// BOTH names joined with "&"; three or more shows the first two then "+N" so
// dense table/desk cells stay readable. One helper → bookings table, daily desk,
// and the owners list all read the same way.
export function formatOwnerLabel(contacts: OwnerContact[] | undefined): string {
  const sorted = (contacts ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const [first, second, ...rest] = sorted;
  if (!first) return '';
  if (!second) return first.name;
  const both = `${first.name} & ${second.name}`;
  return rest.length === 0 ? both : `${both} +${rest.length}`;
}

/**
 * The breed line for a cell holding several dogs — one breed, or the fact that
 * there is more than one.
 *
 * A LIST OF BREEDS IS NOT A BREED. Four dogs of four breeds want 190px on a
 * line 90px wide, and truncated the list says the FIRST dog's breed about all
 * of them, which is worse than saying nothing. "Mixed" is the whole fact in one
 * word, and the register is in the booking for anyone who needs it.
 *
 * BLANKS ARE NOT A BREED EITHER. A dog nobody recorded a breed for does not
 * make a household mixed — that is one breed and some gaps. The line is empty
 * only when no breed was ever filled in, which is the one case where a blank is
 * the truth rather than a shrug (Sir, 2026-08-27).
 *
 * Matching ignores case and surrounding space, so "Collie" and " collie " are
 * one breed and not a mixture. The FIRST spelling recorded is the one shown.
 */
export function breedLabel(dogs: { breed?: string | null }[]): string {
  const seen = new Map<string, string>();
  for (const d of dogs) {
    const breed = (d.breed ?? '').trim();
    if (!breed) continue;
    const key = breed.toLowerCase();
    if (!seen.has(key)) seen.set(key, breed);
  }
  if (seen.size === 0) return '';
  return seen.size === 1 ? [...seen.values()][0]! : 'Mixed';
}

/**
 * The household's telephone number, for the desk row's second line.
 *
 * FIRST BY SORT ORDER, then the first that HAS one. The contacts are ordered
 * deliberately — the first is the one to ring — but a household whose primary
 * contact is an email address still has a number on file, and showing nothing
 * would send staff into the modal to find what the row could have carried.
 */
export function formatOwnerPhone(contacts: OwnerContact[] | undefined): string {
  const sorted = (contacts ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.find((c) => (c.phone ?? '').trim())?.phone?.trim() ?? '';
}

// ---------- Vaccination parsing + rule ----------
//
// The vaccine field is historic free text, month + year, no day — "JUNE 26",
// "JULY'22", "SEPT 26", "APRIL 2026". parseVaccineDue reads it tolerantly: any
// 3+ letter month token followed by a 2- or 4-digit year (apostrophes and stray
// punctuation between are fine). 2-digit years are 20YY.

const VACCINE_MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

export function parseVaccineDue(text: string | null | undefined): { year: number; month: number } | null {
  if (!text) return null;
  const lower = text.trim().toLowerCase();
  // Month-name form. Strip internal spaces first so a typo'd month ("MA Y 25")
  // still reads as one token.
  const m = lower.replace(/\s+/g, '').match(/([a-z]{3,})[^\d]*(\d{2,4})/);
  if (m) {
    const month = VACCINE_MONTHS[m[1]!.slice(0, 3)];
    if (month) {
      let year = parseInt(m[2]!, 10);
      if (year < 100) year += 2000;
      return { year, month };
    }
  }
  // Numeric DD/MM/YYYY (Irish format) — the month is the middle field, day ignored.
  const num = lower.match(/^\d{1,2}\/(\d{1,2})\/(\d{2,4})$/);
  if (num) {
    const month = parseInt(num[1]!, 10);
    if (month >= 1 && month <= 12) {
      let year = parseInt(num[2]!, 10);
      if (year < 100) year += 2000;
      return { year, month };
    }
  }
  return null;
}

// Bridge the stored month-year text and a native <input type="month"> (YYYY-MM):
// the editor shows a month picker but persists the same human "MON YY" text the
// historic data uses, so the parser keeps reading one format. An unparseable
// legacy value maps to an empty picker and is left untouched until re-picked.
export function vaccineDueToMonthInput(text: string | null | undefined): string {
  const d = parseVaccineDue(text);
  return d ? `${d.year}-${String(d.month).padStart(2, '0')}` : '';
}
export function monthInputToVaccineDue(input: string): string {
  if (!input) return '';
  const [y, m] = input.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return '';
  return `${MONTHS_ABBR[m - 1]!.toUpperCase()} ${String(y).slice(-2)}`;
}

export type VaccineState = 'ok' | 'lapsed' | 'missing' | 'unreadable';

// A vaccination is lapsed only when it had already expired before the dog
// arrives: month-granular, the due month strictly before the CHECK-IN month.
// The data is month-only, so a booster due in the arrival month (or later) can't
// be proven lapsed — a due-on-the-28th would still cover an arrival-on-the-1st —
// and is left ok rather than crying wolf. No record on file is a warning in its
// own right (you can't confirm cover); text that won't parse is flagged to re-enter.
export function vaccineState(vaccinesDue: string | null | undefined, stayStartIso: string): VaccineState {
  const text = (vaccinesDue ?? '').trim();
  if (!text) return 'missing';
  const due = parseVaccineDue(text);
  if (!due) return 'unreadable';
  const parts = stayStartIso.split('-');
  const startIdx = Number(parts[0]) * 12 + Number(parts[1]); // year*12 + month(1-12)
  const dueIdx = due.year * 12 + due.month;
  return dueIdx < startIdx ? 'lapsed' : 'ok';
}

// ---------- Health / keep-apart issues (shared row indicator) ----------

// The shared indicator's colour: the most severe issue present, or null for none.
export function issuesSeverity(issues: HealthIssue[]): 'red' | 'amber' | null {
  if (issues.length === 0) return null;
  return issues.some((i) => i.severity === 'red') ? 'red' : 'amber';
}

// ---------- Stat strip (carried from _prototype/bookings.jsx computeStats) ----------

// THE single "in right now" rule (Freddi, 2026-06-17): a dog is physically in the
// building iff its booking is 'arrived' — it stays counted until marked collected.
// Date-agnostic on purpose. Every live headcount (current-dogs, today residence,
// API boarding_now) uses THIS, so they can never drift. (Date-based "who's in on
// day X" is a different projection — see occupancyByDate / spansDay.)
export function isResidentNow(b: { status: string }): boolean {
  return b.status === 'arrived';
}

export type CurrentDogs = Pick<
  StatStripData,
  'currentDogsTotal' | 'currentDogsBig' | 'currentDogsLittle' | 'currentDogsHouse' | 'currentDogsOther'
>;

// Dogs physically in the building RIGHT NOW, split by area — status 'arrived',
// DATE-AGNOSTIC (a dog stays counted until marked collected, so an uncollected
// overstay whose checkout has passed is still in). Counts DOGS via dogsOf;
// mix/house fold into Mix. The caller MUST pass the full arrived set, never a
// date-windowed subset — windowing this drops overstays (the 54/55 class of bug),
// which is exactly why the live count and the date-based arrivals below are
// separate functions over separate populations.
export function currentDogsByArea(bookings: Booking[]): CurrentDogs {
  let big = 0;
  let little = 0;
  let house = 0;
  let other = 0;
  for (const b of bookings) {
    if (!isResidentNow(b)) continue;
    const dogs = dogsOf(b);
    // COUNTED BY BOOKING, and that is a known limit rather than a choice.
    // `house` now has a bucket of its own (dogs-in-areas AC5), and `other`
    // means the booking's dogs genuinely disagree — but a booking-level count
    // cannot say WHICH of them is where. Counting per dog needs each dog's
    // `area` on the wire, which arrives with the booking read; until it does,
    // an `other` booking's dogs land in the Other bucket whole. The strip has
    // always been a headline rather than an occupancy figure, and its total
    // stays exact either way.
    if (b.area === 'big') big += dogs;
    else if (b.area === 'little') little += dogs;
    else if (b.area === 'house') house += dogs;
    else other += dogs;
  }
  return {
    currentDogsTotal: big + little + house + other,
    currentDogsBig: big,
    currentDogsLittle: little,
    currentDogsHouse: house,
    currentDogsOther: other,
  };
}

// The full stat strip over ONE set: live current-dogs (date-agnostic) plus the
// date-based arrival/collection flux around `now`. Over a single set the two
// agree; the Bookings strip deliberately splits them — current dogs from the full
// arrived set, flux from the ±2-day window (see BookingsView) — because the live
// headcount must not be windowed while the flux must be.
export function computeStats(bookings: Booking[], now: Date = new Date()): StatStripData {
  const today = startOfDay(now);
  const yesterday = addDays(today, -1);
  const tomorrow = addDays(today, 1);
  let arrivingYesterday = 0;
  let leavingYesterday = 0;
  let arrivingToday = 0;
  let leavingToday = 0;
  let arrivingTomorrow = 0;
  let leavingTomorrow = 0;
  bookings.forEach((b) => {
    if (isClosedStatus(b.status)) return;
    const ci = startOfDay(b.checkIn);
    const co = startOfDay(b.checkOut);
    // Arrivals / departures count HOUSEHOLDS, not dogs — a drop-off / collection
    // is one handover event per booking regardless of how many dogs ride in the
    // car (Freddi 2026-06-22). The only per-household counts in the app; every
    // headcount (current dogs, occupancy, chips) counts dogs. Date-based; all areas.
    if (ci.getTime() === yesterday.getTime()) arrivingYesterday += 1;
    if (co.getTime() === yesterday.getTime()) leavingYesterday += 1;
    if (ci.getTime() === today.getTime()) arrivingToday += 1;
    if (co.getTime() === today.getTime()) leavingToday += 1;
    if (ci.getTime() === tomorrow.getTime()) arrivingTomorrow += 1;
    if (co.getTime() === tomorrow.getTime()) leavingTomorrow += 1;
  });
  return {
    ...currentDogsByArea(bookings),
    arrivingYesterday,
    leavingYesterday,
    arrivingToday,
    leavingToday,
    arrivingTomorrow,
    leavingTomorrow,
  };
}

// Per-status (and total) DOG counts — the kennel counts dogs, not stays. A stay
// with no dogs still counts as one (`|| 1`), matching computeStats + the server
// /bookings/stats. The pre-load fallback for the toolbar chips.
export function statusCounts(bookings: Booking[]): Record<string, number> {
  const counts: Record<string, number> = {};
  let all = 0;
  for (const b of bookings) {
    const dogs = dogsOf(b);
    all += dogs;
    counts[b.status] = (counts[b.status] ?? 0) + dogs;
  }
  counts.all = all;
  return counts;
}

// ---------- Chart series (carried from _prototype/bookings.jsx chartDailySeries) ----------
// Computed client-side from the bookings list (Tara OQ-4: dogs_by_date is not
// populated by the importer; the prototype computes occupancy + status series
// from the bookings array, not a rollup table).

export function chartDailySeries(bookings: Booking[], from: string, to: string): DailySeriesRow[] {
  const days: DailySeriesRow[] = [];
  let cur = startOfDay(from);
  const end = startOfDay(to);
  while (cur <= end) {
    const iso = isoDate(cur);
    const counts: DailySeriesRow = {
      date: iso,
      unconfirmed: 0,
      confirmed: 0,
      arrived: 0,
      collected: 0,
      cancelled: 0,
      requested: 0,
      declined: 0,
      big: 0,
      little: 0,
      house: 0,
      other: 0,
      total: 0,
    };
    bookings.forEach((b) => {
      const ci = isoDate(b.checkIn);
      const co = isoDate(b.checkOut);
      const n = dogsOf(b);
      // Status buckets: every booking overlapping the day, checkout INCLUSIVE,
      // cancelled INCLUDED — so the status view still shows cancellations.
      if (iso >= ci && iso <= co) {
        counts[b.status] = (counts[b.status] || 0) + n;
        counts.total += n;
      }
      // Area buckets: OCCUPANCY — dogs actually in residence. Shares spansDay
      // with occupancyByDate (cancelled excluded, checkout-exclusive) so the
      // chart's area totals can never drift from the calendar's "N in".
      if (spansDay(b, iso)) {
        // `mix` retired for `other` (dogs-in-areas AC4). Membership against the
        // vocabulary rather than four hand-written comparisons, which is what
        // let `mix` sit here after it stopped existing.
        if (b.area && (AREAS as readonly string[]).includes(b.area)) {
          counts[b.area] = (counts[b.area] || 0) + n;
        }
      }
    });
    days.push(counts);
    cur = addDays(cur, 1);
  }
  return days;
}

// paymentStatusFromMoney / moneyActuallyChanged / paymentStatusToPersist lived
// here until the payments ledger landed. All three existed to let the CLIENT
// decide a booking's payment status and persist it, reconciling a typed deposit
// against a desk verdict the modal could not see. payment_status is now derived
// server-side from SUM(payments.amount) vs total (apps/api/src/payments/ledger.ts),
// so a client-side verdict could only ever disagree with the authority. Removed
// rather than left dormant — a dormant one invites a future save path to start
// asserting a status again.

// Are two date windows the same range? Null is a definite NO, never a crash:
// every hook that holds its data across a window change starts with no window
// at all, and the consumers that compare are exactly the ones that must not
// mistake "nothing yet" for "the one I asked for".
//
// This comparison had been written out by hand in six places. It is not hard —
// which is the point: it is correct in all six until the day the seventh is
// typed with `||` where the others have `&&`, and that copy then reports every
// window as the right one.
export function sameWindow(a: DateWindow | null | undefined, b: DateWindow | null | undefined): boolean {
  return a != null && b != null && a.from === b.from && a.to === b.to;
}
